import { addOpacity } from '../utils/colorUtils';

// Color theme presets
export const colorThemes = {
  // Original teal theme
  teal: {
    primary: {
      main: "#00d4aa", // Vibrant teal/green
      light: "#00e6bb",
      dark: "#00b899",
      contrastText: "#000000",
    },
    accent: "#00d4aa",
  },
  
  // Purple theme based on provided CSS variables
  purple: {
    primary: {
      main: "#8040bf", // hsl(266, 50%, 50%)
      light: "#b380d9", // hsl(266, 50%, 70%)
      dark: "#4d2673", // hsl(266, 50%, 30%)
      contrastText: "#ffffff",
    },
    tertiary: "#eb99d6", // hsl(326, 80%, 80%) - Pink accent
    accent: "#99d6eb", // hsl(206, 80%, 80%) - Blue accent
  },
  
  // Ocean theme
  ocean: {
    primary: {
      main: "#0066cc",
      light: "#3399ff",
      dark: "#004499",
      contrastText: "#ffffff",
    },
    accent: "#00ccff",
  },
  
  // Sunset theme
  sunset: {
    primary: {
      main: "#ff6b6b",
      light: "#ff8787",
      dark: "#ee5a5a",
      contrastText: "#ffffff",
    },
    accent: "#ffab00",
  },
  
  // Dark violet theme based on provided CSS variables
  darkviolet: {
    primary: {
      main: "#a3a3ff", // hsl(236 100% 83%) - primary
      light: "#c2c2ff", // lighter variant
      dark: "#7a7aff", // darker variant
      contrastText: "#000000",
    },
    secondary: {
      main: "#b3a73d", // hsl(51 58% 50%) - secondary
      light: "#d4c85a",
      dark: "#8a7f2e",
      contrastText: "#000000",
    },
    // Additional colors from the palette
    danger: "#e87c7c", // hsl(8 74% 66%)
    warning: "#c9a826", // hsl(52 76% 40%)
    success: "#4fa74f", // hsl(154 54% 47%)
    info: "#82b5f2", // hsl(217 87% 69%)
    // Backgrounds and borders
    backgrounds: {
      dark: "#0a0a14", // hsl(248 100% 5%) - bg-dark
      default: "#141424", // hsl(245 80% 9%) - bg
      light: "#1e1e33", // hsl(241 54% 13%) - bg-light
    },
    text: {
      primary: "#ffffff", // hsl(234 100% 100%) - text
      secondary: "#b5b5e6", // hsl(234 72% 79%) - text-muted
    },
    accent: "#6d6db8", // hsl(239 37% 51%) - highlight
    border: {
      main: "#5757a3", // hsl(241 43% 39%) - border
      muted: "#3d3d70", // hsl(247 62% 27%) - border-muted
    },
  },
};

// Get theme colors based on selected color theme
const getThemeColors = (colorTheme = 'teal') => {
  const theme = colorThemes[colorTheme] || colorThemes.teal;
  return theme;
};

export const landingThemeConfig = {
  // Student Landing Page Theme
  student: (colorTheme = 'teal') => {
    const colors = getThemeColors(colorTheme);
    const isDarkViolet = colorTheme === 'darkviolet';
    
    return {
      // Brand colors
      primary: colors.primary,

      secondary: isDarkViolet ? colors.secondary : {
        main: "#b8bcc8", // Light gray
        light: "#e0e3e8",
        dark: "#8b909f",
      },

      // Status colors (reuse from main theme where appropriate)
      success: {
        main: colors.success || "#4caf50",
        light: "#81c784",
        dark: "#388e3c",
      },

      error: {
        main: colors.danger || "#f44336",
        light: "#e57373",
        dark: "#d32f2f",
      },

      warning: {
        main: colors.warning || "#ff9800",
        light: "#ffb74d",
        dark: "#f57c00",
      },

      info: {
        main: colors.info || "#2196f3",
        light: "#64b5f6",
        dark: "#1976d2",
      },

      // Backgrounds
      background: isDarkViolet ? {
        default: colors.backgrounds.default,
        paper: colors.backgrounds.dark,
        darker: colors.backgrounds.dark,
      } : {
        default: "#1a1a2e", // Dark charcoal
        paper: "#16162a",
        darker: "#0f0f1e", // For demo container
      },

      // Text colors
      text: isDarkViolet ? colors.text : {
        primary: "#ffffff",
        secondary: "#b8bcc8",
        disabled: "#6b7280",
      },

      // Theme-specific colors
      tertiary: colors.tertiary || colors.primary.main,
      accent: colors.accent || colors.primary.main,

      // Component-specific colors
      components: {
        // Hero section
        hero: {
          titleGradientStart: "#ffffff",
          titleGradientEnd: colors.primary.main,
          backgroundGradient: addOpacity(colors.primary.main, 5), // 5% opacity
        },

        // Demo window
        demo: {
          background: isDarkViolet ? colors.backgrounds.dark : "#0f0f1e",
          border: isDarkViolet ? colors.border.muted : "rgba(255, 255, 255, 0.08)",
          glow: addOpacity(colors.primary.main, 15), // 15% opacity
          glowBlur: addOpacity(colors.primary.main, 10), // 10% opacity
          windowButtons: {
            red: "#ff5f57",
            yellow: "#ffbd2e",
            green: "#28ca42",
          },
        },

        // Buttons
        button: {
          primaryShadow: addOpacity(colors.primary.main, 30), // 30% opacity
          primaryShadowHover: addOpacity(colors.primary.main, 50), // 50% opacity
          outlinedHoverBg: addOpacity(colors.primary.main, 8), // 8% opacity
        },

        // Features section
        features: {
          iconBg: addOpacity(colors.primary.main, 8), // 8% opacity
          cardHoverTransform: "translateY(-8px)",
        },

        // CTA section
        cta: {
          backgroundGradient: addOpacity(colors.primary.main, 2), // 2% opacity
          backgroundGradientEnd: addOpacity(colors.primary.main, 1), // 1% opacity
          glowBackground: addOpacity(colors.primary.main, 3), // 3% opacity
        },

        // Footer
        footer: {
          borderColor: "rgba(255, 255, 255, 0.08)",
        },
      },
    };
  },

  // B2B/Educator Landing Page Theme (uses main app theme with overrides)
  b2b: {
    // Inherits from main themeConfig
    primary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },

    secondary: {
      main: "#dc004e",
      light: "#e33371",
      dark: "#9a0036",
    },

    // Light mode backgrounds
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },

    // Light mode text
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },

    // Component overrides for B2B
    components: {
      hero: {
        backgroundGradient: "rgba(25, 118, 210, 0.05)",
        titleGradientStart: "#1976d2",
        titleGradientEnd: "#42a5f5",
      },
    },
  },
};

// Helper function to get theme overrides for student landing
export const getStudentLandingThemeOverrides = (colorTheme = 'teal') => {
  const colors = getThemeColors(colorTheme);
  const isDarkViolet = colorTheme === 'darkviolet';
  
  return {
    palette: {
      mode: 'dark',
      ...landingThemeConfig.student(colorTheme),
      divider: isDarkViolet ? colors.border.muted : 'rgba(255, 255, 255, 0.08)',
    },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (min-width:600px)': {
        fontSize: '3.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '4.5rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '3rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2rem',
      },
    },
    body1: {
      fontSize: '1.125rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 32px',
          fontSize: '1.125rem',
          transition: 'all 0.3s ease',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: `0 0 20px ${theme.palette.components.button.primaryShadow}`,
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
            boxShadow: `0 0 30px ${theme.palette.components.button.primaryShadowHover}`,
            transform: 'translateY(-2px)',
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.components.button.outlinedHoverBg,
          },
        }),
      },
      variants: [
        {
          props: { variant: 'hero' },
          style: ({ theme }) => ({
            padding: theme.spacing(2, 5),
            fontSize: '1.25rem',
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${addOpacity(theme.palette.accent, 10)} 100%)`,
            color: theme.palette.accent,
            border: `1px solid ${addOpacity(theme.palette.accent, 30)}`,
            boxShadow: `0 4px 20px ${addOpacity(theme.palette.accent, 15)}`,
            fontWeight: 700,
            letterSpacing: '0.5px',
            '&:hover': {
              background: `linear-gradient(135deg, ${addOpacity(theme.palette.accent, 15)} 0%, ${addOpacity(theme.palette.accent, 25)} 100%)`,
              borderColor: theme.palette.accent,
              color: theme.palette.text.primary,
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 30px ${addOpacity(theme.palette.accent, 25)}`,
            },
            '& .MuiButton-startIcon': {
              color: 'inherit',
            },
          }),
        },
      ],
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width:1536px)': {
            maxWidth: '1400px',
          },
        },
      },
    },
  },
  spacing: 8,
  };
};